if (!customElements.get('subscription-popup')) {
  customElements.define(
    'subscription-popup',
    class SubscriptionPopup extends DrawerComponent {
      static get observedAttributes() {
        return super.observedAttributes.concat(['data-trigger-open', 'data-repeat-open']);
      }

      constructor() {
        super();
      }

      get requiresBodyAppended() {
        return false;
      }

      connectedCallback() {
        super.connectedCallback();

        this.triggerOpen = this.dataset.triggerOpen;
        this.repeatOpen = this.dataset.repeatOpen;
        this.cookieName = 'hypertheme:popup';

        this.teaserClose = document.querySelector('.subscription-popup__teaser-close');
        if (this.teaserClose) {
          this.toggleTeaser(false);
          const handleTeaserClose = (event) => {
            if (event.cancelable) event.preventDefault();
            event.stopPropagation();
            this.toggleTeaser(true);
          };
          // touchend + preventDefault stops the browser's synthetic click from
          // falling through to whatever ends up under the finger once the
          // button is hidden.
          this.teaserClose.addEventListener('touchend', handleTeaserClose);
          this.teaserClose.addEventListener('click', handleTeaserClose);
        }

        // Pin the ribbon to the viewport. It's `position: fixed`, but when an
        // ancestor section establishes a containing block (e.g. a transform on a
        // parent), fixed positioning resolves against that ancestor instead of the
        // viewport — so the ribbon drifts while scrolling and jumps near the footer.
        // Re-parenting to <body> keeps it locked to the same spot. Moving a node
        // preserves its event listeners, and the open-trigger click was already
        // bound via `controls` in the base DrawerComponent, so the ribbon keeps
        // working. Skipped in the theme editor to avoid duplicating the elements
        // when the section re-renders on edit.
        if (!Shopify.designMode) {
          this.teaserElements.forEach((el) => document.body.appendChild(el));
        }

        window.FoxTheme.DOMready(this.handleOpen.bind(this));
      }

      get teaserElements() {
        return Array.from(
          document.querySelectorAll(
            `.subscription-popup__teaser[aria-controls="${this.id}"], .subscription-popup__teaser-close`
          )
        );
      }

      toggleTeaser(shouldHide, hideDurationHours = 4) {
        const currentTime = new Date().getTime();
        const hideDuration = hideDurationHours * 60 * 60 * 1000;

        if (shouldHide) {
          // Play the right-to-left slide-out, then drop the elements from layout
          // once the 0.4s transition has finished.
          const dismissClass = 'subscription-popup__teaser--dismissing';
          this.teaserElements.forEach((el) => el.classList.add(dismissClass));
          window.setTimeout(() => {
            this.teaserElements.forEach((el) => {
              el.classList.add('hidden');
              el.classList.remove(dismissClass);
            });
          }, 450);
          localStorage.setItem('popup:teaserHidden', 'true');
          localStorage.setItem('popup:teaserHideTime', currentTime);
          localStorage.setItem('popup:teaserHideDuration', hideDuration);
        } else {
          const teaserHidden = localStorage.getItem('popup:teaserHidden');
          const teaserHideTime = localStorage.getItem('popup:teaserHideTime');
          const teaserHideDuration = localStorage.getItem('popup:teaserHideDuration');

          if (teaserHidden === 'true' && teaserHideTime) {
            const timeElapsed = currentTime - parseInt(teaserHideTime);

            if (timeElapsed < parseInt(teaserHideDuration)) {
              this.teaserElements.forEach((el) => el.classList.add('hidden'));
            } else {
              localStorage.removeItem('popup:teaserHidden');
              localStorage.removeItem('popup:teaserHideTime');
              localStorage.removeItem('popup:teaserHideDuration');
              this.teaserElements.forEach((el) => el.classList.remove('hidden'));
            }
          } else {
            this.teaserElements.forEach((el) => el.classList.remove('hidden'));
          }
        }
      }

      prepareToShow() {
        super.prepareToShow();
        this.setRepeatOpen();
      }

      handleOpen() {
        if (Shopify.designMode) return;

        // On mobile, never auto-open (scroll/delay/immediately). The popup should only
        // appear when the visitor taps the corner ribbon (teaser). The teaser click path
        // goes through the base DrawerComponent (onButtonClick -> show) and does not call
        // handleOpen, so returning here leaves the ribbon fully working on mobile.
        if (window.matchMedia(FoxTheme.config.mediaQueryMobile).matches) return;

        let savedData = FoxTheme.utils.getStorage(this.cookieName);
        if (savedData && savedData.opened) {
          return false;
        }

        switch (this.triggerOpen) {
          case 'delay':
            setTimeout(() => {
              this.show();
            }, 5000);
            break;
          case 'scroll_down':
            this.triggerAfterScroll(700, this.show.bind(this));
            break;
          default:
            this.show();
            break;
        }
      }

      triggerAfterScroll(offset = 500, trigger) {
        let opened = false;
        const start = 0;
        window.addEventListener('scroll', (e) => {
          let scrollTop =
            window.pageYOffset || (document.documentElement || document.body.parentNode || document.body).scrollTop;

          if (scrollTop - start > offset && !opened) {
            trigger();
            opened = true;
            return;
          }
        });
      }

      setRepeatOpen() {
        if (Shopify.designMode) return;

        let expires;
        switch (this.repeatOpen) {
          case 'no_repeat':
            expires = 365;
            break;
          case 'every_3_mins':
            expires = 3 / 60 / 24;
            break;
          case 'every_5_mins':
            expires = 5 / 60 / 24;
            break;
          case 'every_10_mins':
            expires = 1 / 6 / 24;
            break;
          case 'every_15_mins':
            expires = 15 / 60 / 24;
            break;
          case 'every_30_mins':
            expires = 1 / 2 / 24;
            break;
          case 'every_1_hr':
            expires = 1 / 24;
            break;
          case 'every_6_hrs':
            expires = 6 / 24;
            break;
          case 'every_12_hrs':
            expires = 1 / 2;
            break;
          case 'every_day':
            expires = 1;
            break;
          case 'every_3_days':
            expires = 3;
            break;
          case 'every_week':
            expires = 7;
            break;
          case 'every_2_weeks':
            expires = 14;
            break;
          case 'every_month':
            expires = 30;
            break;
          default:
            expires = 7;
            break;
        }

        window.FoxTheme.utils.setStorage(this.cookieName, { opened: true }, expires);
      }
    }
  );
}
